import type { FragmentType } from '~/graphql/generated';
import type { Article } from '~/types/Learn/Article';

import { adminArticleLearningAssetFragment, articleDataFragment, articleLearningAssetFragment } from '~/graphql/documents/learn/articles';
import { useFragment } from '~/graphql/generated';
import { fromCampaignDateTime } from '~/utils/campaign';
import { toAdminCurriculumTagList } from '~/utils/learn/curriculumTags';

import { transformAdminContentFile, transformPublicFile } from './fileTransformers';

export const transformArticleAsset = (data: FragmentType<typeof articleLearningAssetFragment>): Article => {
  const asset = useFragment(articleLearningAssetFragment, data);

  return {
    id: asset.id,
    name: asset.name,
    slug: asset.slug,
    publishedAt: asset.publishedAt ?? null,
    lastPublishedAt: asset.lastPublishedAt ?? null,
    firstPublishedAt: asset.firstPublishedAt ?? null,
    publishedContentId: asset.publishedContentId ?? null,
    draftContentId: asset.draftContentId ?? null,
    coverImage: transformPublicFile(asset.coverImage),
    meta: asset.meta ?? null,
    considerationAreas: asset.considerationAreas ?? [],
    publishedContent: asset.publishedContent
      ? { id: asset.publishedContent.id, data: useFragment(articleDataFragment, asset.publishedContent.articleData) ?? null }
      : null,
    draftContent: asset.draftContent
      ? { id: asset.draftContent.id, data: useFragment(articleDataFragment, asset.draftContent.articleData) ?? null }
      : null,
  };
};

/**
 * The same shape off the admin projection, which the content article pages read. The back-date is
 * a calendar day in the cluster timezone, the same anchoring the campaign and expo editors use.
 * `publishedAt` is the stored first publish (kept through unpublish, so the preview matches the
 * next publish), as the public projection shows it when there is no back-date; the raw stamp,
 * re-set on every publish, is `lastPublishedAt`. A live row never stamped reads its latest publish.
 */
export const transformAdminArticleAsset = (data: FragmentType<typeof adminArticleLearningAssetFragment>, clusterTimezone: string): Article => {
  const asset = useFragment(adminArticleLearningAssetFragment, data);

  return {
    id: asset.id,
    name: asset.name,
    slug: asset.slug,
    publishedAt: asset.firstPublishedAt ?? asset.publishedAt ?? null,
    lastPublishedAt: asset.publishedAt ?? null,
    firstPublishedAt: asset.firstPublishedAt ?? null,
    publishedContentId: asset.publishedContentId ?? null,
    draftContentId: asset.draftContentId ?? null,
    coverImage: transformAdminContentFile(asset.coverImage),
    displayPublishedAt: fromCampaignDateTime(asset.displayPublishedAt, clusterTimezone),
    partnerId: asset.partnerId ?? null,
    partner: asset.partner ?? null,
    meta: asset.meta ?? null,
    considerationAreas: asset.considerationAreas ?? [],
    curriculumTags: toAdminCurriculumTagList(asset.curriculumTags),
    publishedContent: asset.publishedContent
      ? { id: asset.publishedContent.id, data: useFragment(articleDataFragment, asset.publishedContent.articleData) ?? null }
      : null,
    draftContent: asset.draftContent
      ? { id: asset.draftContent.id, data: useFragment(articleDataFragment, asset.draftContent.articleData) ?? null }
      : null,
  };
};
